/**
 * Curated display + mono font pairings. Each entry carries everything a
 * consumer needs: the CSS `font-family` name, a full fallback stack, and the
 * Google Fonts query segment so we can build a single <link> for both faces.
 *
 * These are options, not rankings — pick whatever fits the brand.
 */

export interface FontFace {
  /** The primary family name to set in `font-family`, e.g. 'Fraunces'. */
  family: string;
  /** The full CSS stack including fallbacks, e.g. 'Fraunces, Georgia, serif'. */
  stack: string;
  /**
   * The Google Fonts `family=` query segment, including axis/weight spec,
   * e.g. 'Fraunces:opsz,wght@9..144,400..700'. Already URL-shaped per Google's
   * `family=` grammar (spaces as `+`).
   */
  googleFamily: string;
}

export interface FontPairing {
  /** Stable id used in tenant config + URLs, e.g. 'editorial'. */
  key: string;
  /** Human label for the wizard, e.g. 'Editorial'. */
  label: string;
  /** Headings + UI face. */
  display: FontFace;
  /** Code + labels + machine output face. */
  mono: FontFace;
}

const SERIF_FALLBACK = 'Georgia, "Times New Roman", serif';
const SANS_FALLBACK = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MONO_FALLBACK = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export const FONT_PAIRINGS: FontPairing[] = [
  {
    key: 'airy',
    label: 'Airy',
    display: {
      family: 'DM Sans',
      stack: `"DM Sans", ${SANS_FALLBACK}`,
      googleFamily: 'DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700',
    },
    mono: {
      family: 'IBM Plex Mono',
      stack: `"IBM Plex Mono", ${MONO_FALLBACK}`,
      googleFamily: 'IBM+Plex+Mono:wght@400;500;600',
    },
  },
  {
    key: 'editorial',
    label: 'Editorial',
    display: {
      family: 'Fraunces',
      stack: `Fraunces, ${SERIF_FALLBACK}`,
      googleFamily: 'Fraunces:opsz,wght@9..144,400..700',
    },
    mono: {
      family: 'IBM Plex Mono',
      stack: `"IBM Plex Mono", ${MONO_FALLBACK}`,
      googleFamily: 'IBM+Plex+Mono:wght@400;500;600',
    },
  },
  {
    key: 'geometric',
    label: 'Geometric',
    display: {
      family: 'Sora',
      stack: `Sora, ${SANS_FALLBACK}`,
      googleFamily: 'Sora:wght@400;500;600;700',
    },
    mono: {
      family: 'JetBrains Mono',
      stack: `"JetBrains Mono", ${MONO_FALLBACK}`,
      googleFamily: 'JetBrains+Mono:wght@400;500;600',
    },
  },
  {
    key: 'clean',
    label: 'Clean',
    display: {
      family: 'Inter',
      stack: `Inter, ${SANS_FALLBACK}`,
      googleFamily: 'Inter:wght@400;500;600;700',
    },
    mono: {
      family: 'IBM Plex Mono',
      stack: `"IBM Plex Mono", ${MONO_FALLBACK}`,
      googleFamily: 'IBM+Plex+Mono:wght@400;500;600',
    },
  },
  {
    key: 'friendly',
    label: 'Friendly',
    display: {
      family: 'Figtree',
      stack: `Figtree, ${SANS_FALLBACK}`,
      googleFamily: 'Figtree:wght@400;500;600;700',
    },
    mono: {
      family: 'Spline Sans Mono',
      stack: `"Spline Sans Mono", ${MONO_FALLBACK}`,
      googleFamily: 'Spline+Sans+Mono:wght@400;500;600',
    },
  },
  {
    key: 'corporate',
    label: 'Corporate',
    display: {
      family: 'IBM Plex Sans',
      stack: `"IBM Plex Sans", ${SANS_FALLBACK}`,
      googleFamily: 'IBM+Plex+Sans:wght@400;500;600;700',
    },
    mono: {
      family: 'IBM Plex Mono',
      stack: `"IBM Plex Mono", ${MONO_FALLBACK}`,
      googleFamily: 'IBM+Plex+Mono:wght@400;500;600',
    },
  },
  {
    key: 'technical',
    label: 'Technical',
    display: {
      family: 'Space Grotesk',
      stack: `"Space Grotesk", ${SANS_FALLBACK}`,
      googleFamily: 'Space+Grotesk:wght@400;500;600;700',
    },
    mono: {
      family: 'Space Mono',
      stack: `"Space Mono", ${MONO_FALLBACK}`,
      googleFamily: 'Space+Mono:wght@400;700',
    },
  },
];

/** The default pairing for Ghosty's own look (clean & airy rebrand, 2026-07). */
export const DEFAULT_FONT_KEY = 'airy';

/** Resolve a pairing by key, falling back to the default for unknown/undefined. */
export function fontPairing(key: string | undefined): FontPairing {
  const found = key ? FONT_PAIRINGS.find((p) => p.key === key) : undefined;
  return found ?? FONT_PAIRINGS.find((p) => p.key === DEFAULT_FONT_KEY)!;
}

/**
 * Build the Google Fonts stylesheet URL for both faces of a pairing.
 * One <link href> covers display + mono with `display=swap`.
 */
export function googleFontsUrl(pairing: FontPairing): string {
  const families = [pairing.display.googleFamily, pairing.mono.googleFamily];
  const query = families.map((f) => `family=${f}`).join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}
